/**
 * Layer projection of render-only SATELLITES (Codex PR #784 P1 — "satellite
 * layers in viewport masking / layer visibility").
 *
 * A satellite (`./renderSatellites.ts`) is a first-class per-kind renderable
 * that draws through the normal bucket / fill / text pipelines but is
 * deliberately ABSENT from the spatial index — the parent (an INSERT
 * `block-insert` wrapper on the cache-direct path, or an MLEADER with block
 * content) is the single pickable entity. Every layer-set the paper-space
 * viewport pass derives from the spatial index therefore MISSED the
 * satellites' layers:
 *
 *   - `computeVisibleLayers` (layer-level bucket culling per VP) walked only
 *     `spatial.resolve(id).layer` for the VP-visible ids, so a block child on
 *     an explicit layer with NO independent top-level entity on that layer
 *     had its batched line bucket classified "absent" and hidden inside every
 *     paper-space viewport (`applySceneVisibilityMask`'s `visibleLayers`
 *     gate);
 *   - the model / paper layer CENSUS (`modelLayers` / `paperLayers`) walked
 *     `spatial.ids()` only, so that same bucket was ALSO "paper-only" for the
 *     `modelLayers` gate and for the base-layout pre-hide fallback.
 *
 * On the legacy (< 2 MB) branch every block child is its own spatial-index
 * entry with its own layer, so both sets already contained those layers —
 * this module makes the cache-direct result identical by projecting the
 * satellites' layers from the SAME source `expandRenderSatellites` draws
 * them from: the parent → satellites map (`multileaderBlockChildrenRef`,
 * populated by `populateLiftedAndBlockChildren` and maintained by the
 * `FrameLoops` cull in lockstep with the parents).
 *
 * The satellite's OWN `layer` is what is projected (an INSERT child keeps the
 * layer the typed flatten resolved — only layer-"0" children follow the
 * INSERT; an MLEADER child is already re-homed onto the parent layer by
 * `collectRenderSatellites`) PLUS its block-reference layer chain
 * (`renderParentLayers`, round 3 P1 — the INSERT's layer a `(B, parents=[A])`
 * bucket's VP gate also requires), so the projection is exactly the layer
 * chain its bucket is keyed by. The satellite's space is the PARENT's space
 * (a child inherits `spaceId` from the flatten of a parent that lives in
 * that space).
 */

import { addLayerChain } from "../layerChainVisibility";
import type { RenderableEntity, SpatialIndex } from "../types";
import { collectRenderSatellites } from "./renderSatellites";

/** Parent id → its render-only satellites (the `multileaderBlockChildrenRef` map shape). */
export type SatellitesByParent = ReadonlyMap<string, ReadonlyArray<RenderableEntity>>;

/**
 * Add the layers of `parent`'s satellites to `out`, from BOTH sources that
 * describe them:
 *
 *   1. the resolved parent renderable itself — `collectRenderSatellites`, the
 *      single truth `expandRenderSatellites` draws from — read ONLY for an
 *      INSERT wrapper (`block-insert`), where it returns `childRenderables`
 *      verbatim (no allocation; this runs per frame per VP-visible id). An
 *      MLEADER's block content is re-homed onto the PARENT's layer, which is
 *      already in the set, and the collector would allocate a fresh re-homed
 *      copy per call — so for every other kind source 2 suffices;
 *   2. the cull-maintained parent → satellites map (`multileaderBlockChildrenRef`),
 *      the view `FrameLoops` keeps in lockstep with the lifted parents.
 *
 * After bulk-load both agree; reading both keeps the projection correct
 * whichever of them a caller can supply. No-op for a non-satellite parent.
 */
export function addSatelliteLayers(
  parent: RenderableEntity,
  satellitesByParent: SatellitesByParent | undefined,
  out: Set<string>,
): void {
  // Each satellite projects its OWN layer plus its block-reference layer
  // chain (`addLayerChain`, round 3 P1) — the chain is what a
  // `(B, parents=[A])` bucket's VP gate checks for.
  if (parent.geometry.kind === "block-insert") {
    for (const child of collectRenderSatellites(parent)) addLayerChain(child, out);
  }
  const tracked = satellitesByParent?.get(parent.id);
  if (!tracked) return;
  for (const child of tracked) addLayerChain(child, out);
}

/**
 * Layers with ≥1 model-space entity and layers with ≥1 paper-space entity —
 * the census the paper-space passes key their "paper-only bucket" gate on
 * (`applySceneVisibilityMask`'s `modelLayers`, the base-layout pre-hide
 * fallback). Walks every spatial-index entity AND its satellites (the
 * satellite counts toward its PARENT's space).
 */
export function computeSpaceLayerCensus(
  spatial: SpatialIndex | null,
  satellitesByParent?: SatellitesByParent,
): { modelLayers: Set<string>; paperLayers: Set<string> } {
  const modelLayers = new Set<string>();
  const paperLayers = new Set<string>();
  if (!spatial) return { modelLayers, paperLayers };
  for (const id of spatial.ids()) {
    const e = spatial.resolve(id);
    if (!e) continue;
    const target = e.spaceId === "model" ? modelLayers : paperLayers;
    addLayerChain(e, target);
    addSatelliteLayers(e, satellitesByParent, target);
  }
  return { modelLayers, paperLayers };
}
